// Phase 4b — transform module. Exports the composable op + verb
// arrays (`transformOps`, `transformVerbs`). Consumers compose via
// `createOpTable([...builtins, ...transformOps])` /
// `createVerbTable([...builtinVerbs, ...transformVerbs])`.
//
// Internal helpers (`AggSpec`, `aggContextScope`, `bucketTimestamp`,
// `compileAggPair`, …) are imported directly from their source files;
// this module exposes only the composable op/verb arrays plus the
// scope so the chat-side renderer can list agg-context funcs.

import { defineScope, pipelineVerbs, type Scope } from '../../op-meta.ts';
import { aggContextScope } from './agg-funcs.ts';
import { desc } from './desc.ts';
import { dateBucket, formatDate, toDate } from './scalar-funcs.ts';
import { stringFuncs } from './string-funcs.ts';
import { aggregate } from './verbs/aggregate.ts';
import { join } from './verbs/join.ts';
import { fold, pivot, spread, unroll } from './verbs/reshape.ts';
import { sort } from './verbs/sort.ts';
import { timeBucket } from './verbs/time-bucket.ts';
import { unnest } from './verbs/unnest.ts';

/** Top-level transform ops — scalar/reference funcs callable from anywhere. */
export const transformOps = [desc, toDate, dateBucket, formatDate, ...stringFuncs] as const;

/** Pipeline verbs contributed by the transform module. */
export const transformVerbs = [sort, aggregate, join, unnest, timeBucket, pivot, fold, spread, unroll] as const;

/**
 * Doc-walker scope for the Phase 4b pipeline verbs. They live in the
 * `pipelineVerbs` registry, so `<ExprInstructions>` cannot reach them
 * from an `opMeta` root via `childScopes` — a transform tool passes
 * this as an `extraScopes` entry. `aggregate` / `time-bucket` declare
 * `childScopes: [aggContextScope]`, so the agg-context scope is pulled
 * in transitively once this scope is walked (no separate wiring).
 */
export const transformVerbScope: Scope<unknown> = defineScope({
  name: 'Transform verbs',
  description:
    'Reachable inside `(transform <dataset> <verb> …)`. Each verb threads the dataset through one step of the pipeline.',
  registry: pipelineVerbs,
  schemas: transformVerbs,
});

/** Re-export the agg-context scope for doc-walker registration on parent verbs. */
export { aggContextScope };
